import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { PageLayout } from '../../components/common/PageLayout'
import { Section, StatCard } from '../../components/common/DesignComponents'
import { Breadcrumb } from '../../components/common/NavigationComponents'
import { AchievementBadge, FilterChips, Alert } from '../../components/common/LayoutPatterns'

interface Achievement {
  id: string
  name: string
  description: string
  icon: string
  xpReward: number
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'
  isUnlocked: boolean
  unlockedAt?: Date
}

interface AchievementsResponse {
  achievements: Achievement[]
  unlockedCount: number
  totalCount: number
}

export const AchievementPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>()
  const navigate = useNavigate()

  const [achievements, setAchievements] = useState<Achievement[]>([])
  const [data, setData] = useState<AchievementsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null)
  const [filter, setFilter] = useState<'all' | 'unlocked' | 'locked'>('all')

  useEffect(() => {
    fetchAchievements()
  }, [childId])

  const fetchAchievements = async () => {
    try {
      setLoading(true)
      const response = await axiosClient.get(`/api/children/${childId}/achievements`)
      if (response.data.success) {
        setData(response.data.data)
        setAchievements(response.data.data.achievements)
      }
    } catch (err) {
      setError('Failed to load achievements')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const getFilteredAchievements = () => {
    switch (filter) {
      case 'unlocked':
        return achievements.filter((a) => a.isUnlocked)
      case 'locked':
        return achievements.filter((a) => !a.isUnlocked)
      default:
        return achievements
    }
  }

  const filteredAchievements = getFilteredAchievements()

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin text-5xl mb-4">🏅</div>
            <p className="text-purple-600 font-semibold">Loading achievements...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout>
        <Section>
          <div className="text-center py-12">
            <p className="text-red-600 mb-4 text-lg">❌ {error}</p>
            <button
              onClick={() => navigate('/children')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
            >
              Back to Children
            </button>
          </div>
        </Section>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <Section>
        {/* Header */}
        <Breadcrumb
          items={[
            { label: 'Children', href: '/children' },
            { label: 'Achievements', current: true },
          ]}
          className="mb-6"
        />

        <div className="mb-8">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-6">
            🏅 Achievements
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              label="Unlocked"
              value={data?.unlockedCount.toString() || '0'}
              icon="✅"
              className="bg-gradient-to-br from-green-50 to-emerald-50"
            />
            <StatCard
              label="Total"
              value={data?.totalCount.toString() || '0'}
              icon="🎯"
              className="bg-gradient-to-br from-blue-50 to-indigo-50"
            />
            <StatCard
              label="Completion"
              value={`${Math.round(((data?.unlockedCount || 0) / (data?.totalCount || 1)) * 100)}%`}
              icon="📊"
              className="bg-gradient-to-br from-purple-50 to-pink-50"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-3 flex-wrap">
          <FilterChips
            chips={[
              { label: 'All', selected: filter === 'all', onClick: () => setFilter('all') },
              { label: '✅ Unlocked', selected: filter === 'unlocked', onClick: () => setFilter('unlocked') },
              { label: '🔒 Locked', selected: filter === 'locked', onClick: () => setFilter('locked') },
            ]}
          />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Achievements Grid */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-purple-600 mb-6">
              {filter === 'all'
                ? 'All Achievements'
                : filter === 'unlocked'
                ? '✅ Unlocked Achievements'
                : '🔒 Locked Achievements'}
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {getFilteredAchievements().map((achievement) => (
                <div
                  key={achievement.id}
                  onClick={() => setSelectedAchievement(achievement)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition text-center ${
                    selectedAchievement?.id === achievement.id
                      ? 'border-purple-400 bg-purple-100'
                      : 'border-purple-200 bg-white hover:border-purple-400'
                  } ${!achievement.isUnlocked ? 'opacity-60' : ''}`}
                >
                  <div
                    className={`text-4xl mb-2 ${
                      !achievement.isUnlocked ? 'grayscale' : ''
                    }`}
                  >
                    {achievement.isUnlocked ? achievement.icon : '🔒'}
                  </div>
                  <p className={`text-xs font-bold mb-1 ${achievement.isUnlocked ? 'text-purple-600' : 'text-gray-500'}`}>
                    {achievement.rarity.toUpperCase()}
                  </p>
                  <p className={`text-sm font-semibold ${achievement.isUnlocked ? 'text-purple-600' : 'text-gray-500'}`}>
                    {achievement.name}
                  </p>
                  {achievement.isUnlocked && (
                    <p className="text-xs text-green-600 mt-2 font-bold">✓ Unlocked</p>
                  )}
                </div>
              ))}
            </div>

            {getFilteredAchievements().length === 0 && (
              <div className="text-center py-12 bg-purple-50 rounded-xl border-2 border-dashed border-purple-200">
                <p className="text-gray-600 text-lg">
                  📭 No {filter === 'all' ? 'achievements' : `${filter} achievements`} found
                </p>
              </div>
            )}
          </div>

          {/* Achievement Details */}
          <div className="lg:col-span-1">
            {selectedAchievement ? (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 p-6 sticky top-6">
                <div className="text-center mb-6">
                  <div className={`text-6xl mb-4 ${!selectedAchievement.isUnlocked ? 'grayscale' : ''}`}>
                    {selectedAchievement.isUnlocked ? selectedAchievement.icon : '🔒'}
                  </div>
                  <h3 className="text-2xl font-bold text-purple-600 mb-2">
                    {selectedAchievement.name}
                  </h3>
                  <div className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-600">
                    {selectedAchievement.rarity.toUpperCase()}
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 text-center">{selectedAchievement.description}</p>
                </div>

                <div className="mb-6 p-4 bg-white rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1 font-semibold">Experience Reward</p>
                  <p className="text-2xl font-bold text-blue-600">⭐ {selectedAchievement.xpReward} XP</p>
                </div>

                {selectedAchievement.isUnlocked && selectedAchievement.unlockedAt && (
                  <div className="p-4 bg-green-50 rounded-lg text-center border border-green-200 mb-4">
                    <p className="text-sm text-green-700 font-semibold">
                      ✓ Unlocked on{' '}
                      {new Date(selectedAchievement.unlockedAt).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {!selectedAchievement.isUnlocked && (
                  <div className="p-4 bg-gray-50 rounded-lg text-center border border-gray-200 mb-4">
                    <p className="text-sm text-gray-600 font-semibold">🎮 Keep playing to unlock this achievement!</p>
                  </div>
                )}

                <div className="p-4 bg-white rounded-lg border-l-4 border-purple-600">
                  <p className="text-xs text-gray-600 mb-2 font-bold">STATUS</p>
                  <p className={`font-bold ${selectedAchievement.isUnlocked ? 'text-green-600' : 'text-gray-600'}`}>
                    {selectedAchievement.isUnlocked ? '✅ UNLOCKED' : '🔒 LOCKED'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200 p-6 text-center">
                <p className="text-gray-600">👆 Select an achievement to view details</p>
              </div>
            )}
          </div>
        </div>
      </Section>
    </PageLayout>
  )
}

export default AchievementPage
